import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "@/components/Layout";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/Card";
import { EmptyState } from "@/components/EmptyState";
import {
  HELP_ARTICLES,
  HELP_CATEGORIES,
  getArticlesByCategory,
  searchArticles,
  type HelpArticle,
  type HelpCategoryId,
} from "@/data/helpArticles";

/**
 * Help Center index.
 *
 * - Search across all articles (title-weighted)
 * - Categorized card grid when no search is active
 * - Always-visible CTAs: message care team + view all articles
 *
 * Static content — no data fetching. Server-rendered would be ideal for SEO
 * but the Help Center is auth-gated, so SSR doesn't buy us anything.
 */
export default function Help() {
  const [query, setQuery] = useState("");
  const trimmed = query.trim();

  const results: HelpArticle[] = useMemo(
    () => (trimmed ? searchArticles(trimmed) : []),
    [trimmed],
  );

  return (
    <AppLayout>
      <PageHeader
        title="Help Center"
        subtitle="Answers to common questions about your care, medication, billing, and account."
      />

      {/* Search bar — prominent because most patients arrive with a specific
          question rather than browsing categories. */}
      <div className="mb-6">
        <div className="relative">
          <SearchIcon />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help articles — e.g., 'side effects', 'refund', 'cancel'…"
            className="w-full pl-11 pr-4 py-3 text-[15px] bg-white border border-fh-border rounded-[12px] focus:outline-none focus:ring-2 focus:ring-fh-green focus:border-fh-green placeholder:text-fh-gray-light"
            aria-label="Search help articles"
          />
        </div>
      </div>

      {trimmed ? (
        <SearchResults results={results} query={trimmed} />
      ) : (
        <CategoryGrid />
      )}

      {/* Always-on CTA: if a patient can't find what they need, route them to
          the messaging surface. This is the most important fallback for any
          help center — every healthcare support failure traces back to "I
          couldn't find the answer and didn't know how to ask." */}
      <div className="mt-10 rounded-tile border border-fh-green-light bg-fh-sage/30 p-6">
        <h3 className="font-serif text-[18px] text-fh-ink mb-1.5">
          Still need help?
        </h3>
        <p className="text-[14px] text-fh-gray mb-3 m-0 leading-relaxed">
          Send a message to your care team — we typically reply within 1 business day.
          For medical emergencies, call <strong>911</strong>.
        </p>
        <Link
          to="/messages"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] bg-fh-green-dark text-white text-[14px] font-semibold hover:bg-fh-green transition"
        >
          Message care team →
        </Link>
      </div>
    </AppLayout>
  );
}

function CategoryGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {HELP_CATEGORIES.map((cat) => {
        const arts = getArticlesByCategory(cat.id);
        return <CategoryCard key={cat.id} catId={cat.id} title={cat.label} blurb={cat.blurb} articles={arts} />;
      })}
    </div>
  );
}

function CategoryCard({
  catId,
  title,
  blurb,
  articles,
}: {
  catId: HelpCategoryId;
  title: string;
  blurb: string;
  articles: HelpArticle[];
}) {
  // Cap the inline list at 3; rest are accessible via the article links from
  // search or from the parent category. Keeps card heights uniform across
  // categories of varied size.
  const preview = articles.slice(0, 3);
  const overflow = articles.length - preview.length;
  return (
    <Card>
      <div className="flex items-start justify-between mb-2 gap-3">
        <h3 className="font-serif text-[17px] text-fh-ink m-0">{title}</h3>
        <span className="text-[11px] uppercase tracking-wider text-fh-gray font-semibold">
          {articles.length}
        </span>
      </div>
      <p className="text-[13px] text-fh-gray m-0 mb-3 leading-snug">{blurb}</p>
      <ul className="space-y-1.5 mb-1">
        {preview.map((a) => (
          <li key={a.id}>
            <Link
              to={`/help/${a.id}`}
              className="text-[14px] text-fh-green-dark hover:text-fh-green underline-offset-2 hover:underline"
            >
              {a.title}
            </Link>
          </li>
        ))}
      </ul>
      {overflow > 0 && (
        <p className="text-[12px] text-fh-gray-light m-0 mt-1">
          + {overflow} more {overflow === 1 ? "article" : "articles"} — use search above to find them
        </p>
      )}
      {/* anchor for category — could turn this into /help/category/:id later */}
      <span id={`cat-${catId}`} className="sr-only" />
    </Card>
  );
}

function SearchResults({ results, query }: { results: HelpArticle[]; query: string }) {
  if (results.length === 0) {
    return (
      <EmptyState
        title={`No results for "${query}"`}
        body="Try a shorter or different search term — or message your care team and we'll get back to you within 1 business day."
      />
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-[13px] text-fh-gray m-0 mb-3">
        {results.length} {results.length === 1 ? "result" : "results"} for <strong className="text-fh-ink">"{query}"</strong>
      </p>
      {results.map((a) => (
        <Link
          key={a.id}
          to={`/help/${a.id}`}
          className="block p-4 bg-white border border-fh-border rounded-[12px] hover:border-fh-green hover:shadow-card transition"
        >
          <div className="flex items-baseline justify-between gap-3 mb-1">
            <h4 className="font-serif text-[16px] text-fh-ink m-0">{a.title}</h4>
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-fh-gray whitespace-nowrap">
              {HELP_CATEGORIES.find((c) => c.id === a.category)?.label}
            </span>
          </div>
          <p className="text-[13.5px] text-fh-gray m-0 leading-snug">{a.summary}</p>
        </Link>
      ))}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      aria-hidden="true"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-fh-gray pointer-events-none"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

// Keep this export so other modules can import HELP_ARTICLES from here if
// preferred — small ergonomic win, no functional impact.
export { HELP_ARTICLES };
